// ============================================================
// 阶段一 T11：AI 生成抽屉
// - 需求描述 + 基座选择 + 生成范围 + 高级（设备/点位映射）
// - 任务轮询（pollMs 可配）显示阶段/进度；成功后可打开 3D 编辑器/大屏
// - 生成记录（最近 10 条），可继续打开历史草稿
// ============================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  Divider,
  Drawer,
  Form,
  Input,
  Progress,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import {
  cancelAiGenerateTask,
  createAiGenerateTask,
  fetchAiGenerateBases,
  fetchAiGenerateTask,
  fetchAiGenerateTasks,
  type AiGenerateBaseItem,
  type AiGenerateTask,
} from '../../api/aiGenerate';

const { Text } = Typography;

export interface AiGenerateDrawerProps {
  open: boolean;
  onClose: () => void;
  /** 轮询间隔（测试可注入更小值） */
  pollMs?: number;
  /** A3：深链预选基座（/projects?ai=1&baseId=xxx，基座列表加载后写入表单） */
  initialBaseId?: string;
}

const STAGE_LABEL: Record<string, string> = {
  queued: '排队中',
  intent: '理解需求',
  generate: '生成工程',
  validate: '校验结果',
  write: '写入草稿',
  done: '完成',
  timeout: '超时',
  failed: '失败',
  canceled: '已取消',
};

const STATUS_COLOR: Record<string, string> = {
  queued: 'default',
  running: 'processing',
  succeeded: 'success',
  failed: 'error',
  canceled: 'warning',
};

const STATUS_LABEL: Record<string, string> = {
  queued: '排队中',
  running: '生成中',
  succeeded: '已成功',
  failed: '失败',
  canceled: '已取消',
};

function apiErrorMessage(e: unknown): string {
  const err = e as { response?: { data?: { message?: string | string[] } }; message?: string };
  const msg = err?.response?.data?.message;
  if (Array.isArray(msg)) return msg[0];
  return msg || err?.message || '请求失败';
}

export default function AiGenerateDrawer({ open, onClose, pollMs = 3000, initialBaseId }: AiGenerateDrawerProps) {
  const [form] = Form.useForm();
  const [bases, setBases] = useState<AiGenerateBaseItem[]>([]);
  const [basesLoading, setBasesLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [task, setTask] = useState<AiGenerateTask | null>(null);
  const [history, setHistory] = useState<AiGenerateTask[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      setHistory(await fetchAiGenerateTasks(10));
    } catch {
      /* 列表失败不阻塞主流程 */
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // 打开时加载基座与历史
  useEffect(() => {
    if (!open) return;
    setBasesLoading(true);
    fetchAiGenerateBases()
      .then((list) => {
        setBases(list);
        // A3：深链预选基座（仅在可用时）
        if (initialBaseId) {
          const hit = list.find((b) => b.id === initialBaseId && b.accessible !== false);
          if (hit) form.setFieldsValue({ baseId: hit.id });
        }
      })
      .catch((e) => message.warning(apiErrorMessage(e)))
      .finally(() => setBasesLoading(false));
    void loadHistory();
  }, [open, loadHistory, initialBaseId, form]);

  // 任务轮询（终态即停）
  useEffect(() => {
    if (!taskId) return;
    let stopped = false;
    const tick = async () => {
      try {
        const t = await fetchAiGenerateTask(taskId);
        if (stopped) return;
        setTask(t);
        if (t.status === 'succeeded' || t.status === 'failed' || t.status === 'canceled') {
          void loadHistory();
          return;
        }
      } catch {
        /* 网络抖动继续轮询 */
      }
      if (!stopped) timerRef.current = setTimeout(tick, pollMs);
    };
    timerRef.current = setTimeout(tick, 500);
    return () => {
      stopped = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [taskId, pollMs, loadHistory]);

  const handleCreate = async () => {
    let values: { prompt: string; baseId?: string; scopes?: string[]; deviceId?: string; bindPointKey?: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setCreating(true);
    try {
      const r = await createAiGenerateTask({
        prompt: values.prompt,
        ...(values.baseId ? { baseId: values.baseId } : {}),
        ...(values.scopes ? { scopes: values.scopes } : {}),
        ...(values.deviceId ? { deviceId: values.deviceId } : {}),
        ...(values.bindPointKey ? { bindPointKey: values.bindPointKey } : {}),
      });
      setTaskId(r.taskId);
      setTask({
        id: r.taskId,
        status: r.status,
        stage: 'queued',
        progress: 5,
        baseId: values.baseId ?? null,
        prompt: values.prompt,
        warnings: [],
        error: null,
        result: { sceneId: null, pageId: null, refs: null },
        createdAt: new Date().toISOString(),
        startedAt: null,
        finishedAt: null,
      });
      message.success('已创建生成任务，正在生成草稿…');
    } catch (e) {
      message.error(apiErrorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = async () => {
    if (!taskId) return;
    try {
      await cancelAiGenerateTask(taskId);
      message.info('已取消');
      void loadHistory();
    } catch (e) {
      message.warning(apiErrorMessage(e));
    }
  };

  const openScene = (t: AiGenerateTask) => {
    if (t.result.sceneId) window.open(`/editor/scene/${t.result.sceneId}`, '_blank');
  };
  const openDashboard = (t: AiGenerateTask) => {
    if (t.result.sceneId && t.result.pageId) {
      window.open(`/editor/scene/${t.result.sceneId}/dashboard/${t.result.pageId}`, '_blank');
    }
  };

  const renderTaskActions = (t: AiGenerateTask) => (
    <Space>
      {t.status === 'succeeded' && t.result.sceneId && (
        <Button type="primary" size="small" onClick={() => openScene(t)}>
          打开 3D 编辑器
        </Button>
      )}
      {t.status === 'succeeded' && t.result.sceneId && t.result.pageId && (
        <Button size="small" onClick={() => openDashboard(t)}>
          打开大屏
        </Button>
      )}
      {t.status === 'queued' && (
        <Button size="small" danger onClick={handleCancel}>
          取消
        </Button>
      )}
    </Space>
  );

  return (
    <Drawer title="AI 生成" size={520} open={open} onClose={onClose} destroyOnHidden>
      {bases.length === 0 && !basesLoading ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          title="当前没有可用的生成基座"
          description="可能是 AI 生成未启用（AI_GENERATE_ENABLED）或当前套餐不含生成能力；升级套餐后可用。"
        />
      ) : null}

      <Form form={form} layout="vertical" initialValues={{ scopes: ['scene', 'dashboard', 'dataflow'] }}>
        <Form.Item
          name="prompt"
          label="需求描述"
          rules={[{ required: true, message: '请输入需求描述' }, { max: 2000, message: '≤2000 字' }]}
        >
          <Input.TextArea
            rows={4}
            showCount
            maxLength={2000}
            placeholder="例如：生成一个污水处理厂孪生，包含水池、管道，大屏增加进出水流量、pH 指标和告警"
          />
        </Form.Item>
        <Form.Item name="baseId" label="行业基座">
          <Select
            allowClear
            loading={basesLoading}
            placeholder="默认使用第一个可用基座"
            options={bases.map((b) => ({
              value: b.id,
              label: `${b.name}（${b.tier}${b.trial ? ' · 试用' : ''}${b.accessible === false ? ` · ${b.lockReason === 'requires_addon' ? '需加购' : '需购买'}` : ''}）`,
              disabled: b.accessible === false,
            }))}
          />
        </Form.Item>
        <Form.Item name="scopes" label="生成范围">
          <Checkbox.Group
            options={[
              { label: '3D 场景', value: 'scene' },
              { label: '可视化大屏', value: 'dashboard' },
              { label: '数据集与规则', value: 'dataflow' },
            ]}
          />
        </Form.Item>
        <Collapse
          ghost
          items={[
            {
              key: 'adv',
              label: '高级：设备与点位映射（可选）',
              children: (
                <>
                  <Form.Item name="deviceId" label="设备 IMEI" style={{ marginBottom: 12 }}>
                    <Input placeholder="如 AUDIT9000000001（内置演示设备）" allowClear />
                  </Form.Item>
                  <Form.Item name="bindPointKey" label="数据点位 key" style={{ marginBottom: 0 }}>
                    <Input placeholder="如 io.ai0（用于场景模型联动）" allowClear />
                  </Form.Item>
                </>
              ),
            },
          ]}
        />
        <Button
          type="primary"
          block
          icon={<ThunderboltOutlined />}
          loading={creating}
          disabled={bases.length === 0}
          onClick={handleCreate}
        >
          开始生成
        </Button>
      </Form>

      {task && (
        <Card size="small" style={{ marginTop: 16 }} title={`生成任务 · ${STATUS_LABEL[task.status] || task.status}`}>
          <Progress
            percent={task.progress}
            status={task.status === 'failed' ? 'exception' : task.status === 'succeeded' ? 'success' : 'active'}
          />
          <Space orientation="vertical" size={4} style={{ width: '100%', marginTop: 8 }}>
            <Text type="secondary">
              状态：{STAGE_LABEL[task.stage] || task.stage}
              {task.status === 'running' ? '（进行中）' : ''}
            </Text>
            {task.error ? <Text type="danger">{task.error}</Text> : null}
            {task.warnings?.length ? <Text type="warning">提示：{task.warnings.join('；')}</Text> : null}
            {renderTaskActions(task)}
          </Space>
        </Card>
      )}

      <Divider titlePlacement="left" plain>
        最近生成
      </Divider>
      <div>
        {historyLoading ? (
          <Text type="secondary">加载中…</Text>
        ) : history.length === 0 ? (
          <Text type="secondary">暂无生成记录</Text>
        ) : (
          history.map((t) => (
            <div
              key={t.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                padding: '6px 0',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <Space size={6}>
                <Tag color={STATUS_COLOR[t.status]}>{STAGE_LABEL[t.stage] || t.status}</Tag>
                <Text style={{ maxWidth: 220 }} ellipsis={{ tooltip: t.prompt }}>
                  {t.prompt}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {new Date(t.createdAt).toLocaleString()}
                </Text>
              </Space>
              {t.status === 'succeeded' && t.result.sceneId ? <a onClick={() => openScene(t)}>打开</a> : null}
            </div>
          ))
        )}
      </div>
    </Drawer>
  );
}
